// REFACTORED: 2026-02-02
// FROM src/components/layout/DashboardLayout.tsx

// ============================================================================
// FILTER UTILITIES
// ============================================================================
// These filter/sort/pagination helpers are imported and used in:
//   src/components/dri-insights/DRIInsights.tsx
// They centralize handler logic so DRIInsights (and other pages) only wire state.
// ============================================================================

import React from 'react';
import { FilterRequest } from '../types/api';

// ----------------------------------------------------------------------------
// Page-level filter checks (used by DRIInsights)
// ----------------------------------------------------------------------------

/**
 * Check if filters have been changed but not yet applied
 */
export const hasFiltersChanged = (filters: FilterRequest, activeFilters: FilterRequest): boolean => {
  return JSON.stringify(filters) !== JSON.stringify(activeFilters);
};

/**
 * Check if user has no business units assigned
 */
export const hasNoBusinessUnits = (filterOptions: { business_units?: string[] } | null | undefined): boolean => {
  return !!(filterOptions && filterOptions.business_units && filterOptions.business_units.length === 0);
};

// ----------------------------------------------------------------------------
// Generic column filter state shape (for hasActiveFilter)
// ----------------------------------------------------------------------------

export type ColumnFilterState = {
  text?: string;
  min?: number | null;
  max?: number | null;
  selectedValues?: string[];
  startDate?: Date | null;
  endDate?: Date | null;
};

/**
 * Check if a column has an active filter (used by DRIInsights Individual Details / BU tables).
 * Imported in: src/components/dri-insights/DRIInsights.tsx
 */
export const hasActiveFilter = (
  filters: Record<string, ColumnFilterState>,
  columnId: string
): boolean => {
  const filter = filters[columnId];
  if (!filter) return false;
  return (
    (filter.text !== undefined && filter.text !== '') ||
    (filter.min !== null && filter.min !== undefined) ||
    (filter.max !== null && filter.max !== undefined) ||
    (filter.selectedValues !== undefined && filter.selectedValues.length > 0) ||
    (filter.startDate !== null && filter.startDate !== undefined) ||
    (filter.endDate !== null && filter.endDate !== undefined)
  );
};

// ----------------------------------------------------------------------------
// Sort handlers (used by DRIInsights: BU table, Individual Details table)
// ----------------------------------------------------------------------------

export type SortDirection = 'asc' | 'desc';

/**
 * Create a sort handler that toggles direction on same column or sets new column.
 * Used in DRIInsights for handleBuSort, handleSort.
 */
export function createSortHandler<TColumn extends string | null>(
  getSortColumn: () => TColumn,
  setSortColumn: (col: TColumn) => void,
  getSortDirection: () => SortDirection,
  setSortDirection: (dir: SortDirection) => void,
  setPage?: (page: number) => void
) {
  return (column: TColumn) => {
    if (getSortColumn() === column) {
      setSortDirection(getSortDirection() === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
    if (setPage) setPage(0);
  };
}

/**
 * Legacy hook for optional header sort affordance. Product standard is no visible sort chevrons.
 * @deprecated Prefer clickable header text only; keep for API compatibility.
 */
export function getSortIcon<TColumn extends string | null>(
  _sortColumn: TColumn,
  _column: TColumn,
  _sortDirection: SortDirection
): React.ReactNode {
  return null;
}

// ----------------------------------------------------------------------------
// Pagination handlers (used by DRIInsights: main grid, BU table)
// ----------------------------------------------------------------------------

/**
 * Create page change handler.
 */
export function createChangePageHandler(setPage: (page: number) => void) {
  return (_event: unknown, newPage: number) => setPage(newPage);
}

/**
 * Create rows-per-page change handler (resets to page 0).
 */
export function createChangeRowsPerPageHandler(
  setRowsPerPage: (rows: number) => void,
  setPage: (page: number) => void
) {
  return (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };
}

// ----------------------------------------------------------------------------
// Filter anchor / open-close (used by DRIInsights Individual Details)
// ----------------------------------------------------------------------------

/**
 * Create filter click handler (open menu and optionally init temp values).
 */
export function createFilterClickHandler(
  setFilterAnchorEl: (el: Record<string, HTMLElement | null>) => void,
  filterAnchorEl: Record<string, HTMLElement | null>,
  options?: {
    tempSelectedValues?: Record<string, string[]>;
    setTempSelectedValues?: (v: Record<string, string[]>) => void;
    columnFilters?: Record<string, ColumnFilterState>;
    tempNumericFilters?: Record<string, { min?: number | null; max?: number | null }>;
    setTempNumericFilters?: (v: Record<string, { min?: number | null; max?: number | null }>) => void;
  }
) {
  return (
    event: React.MouseEvent<HTMLElement>,
    columnId: string,
    useCheckbox?: boolean,
    isNumeric?: boolean
  ) => {
    setFilterAnchorEl({ ...filterAnchorEl, [columnId]: event.currentTarget });

    if (options?.setTempSelectedValues && options?.tempSelectedValues !== undefined && useCheckbox) {
      const currentApplied = options.columnFilters?.[columnId]?.selectedValues || [];
      if (options.tempSelectedValues[columnId] === undefined) {
        options.setTempSelectedValues({
          ...options.tempSelectedValues,
          [columnId]: currentApplied,
        });
      }
    }

    if (options?.setTempNumericFilters && options?.tempNumericFilters !== undefined && isNumeric) {
      const currentApplied = options.columnFilters?.[columnId] || {};
      if (options.tempNumericFilters[columnId] === undefined) {
        options.setTempNumericFilters({
          ...options.tempNumericFilters,
          [columnId]: {
            min: currentApplied.min !== undefined ? currentApplied.min : null,
            max: currentApplied.max !== undefined ? currentApplied.max : null,
          },
        });
      }
    }
  };
}

/**
 * Create filter close handler (close menu).
 */
export function createFilterCloseHandler(
  setFilterAnchorEl: (el: Record<string, HTMLElement | null>) => void,
  filterAnchorEl: Record<string, HTMLElement | null>
) {
  return (columnId: string) => {
    setFilterAnchorEl({ ...filterAnchorEl, [columnId]: null });
  };
}

// ----------------------------------------------------------------------------
// Numeric filter handlers (used by DRIInsights Individual Details)
// ----------------------------------------------------------------------------

/**
 * Create numeric filter change handler (min/max temp state).
 */
export function createNumericFilterChangeHandler(
  setTempNumericFilters: (v: Record<string, { min?: number | null; max?: number | null }>) => void,
  tempNumericFilters: Record<string, { min?: number | null; max?: number | null }>
) {
  return (columnId: string, type: 'min' | 'max', value: string) => {
    const numValue = value === '' ? null : parseFloat(value);
    setTempNumericFilters({
      ...tempNumericFilters,
      [columnId]: {
        ...tempNumericFilters[columnId],
        [type]: numValue,
      },
    });
  };
}

/**
 * Create apply numeric filter handler (commit temp to filters and close).
 */
export function createApplyNumericFilterHandler(
  setColumnFilters: (v: Record<string, ColumnFilterState>) => void,
  columnFilters: Record<string, ColumnFilterState>,
  tempNumericFilters: Record<string, { min?: number | null; max?: number | null }>,
  closeFilter: (columnId: string) => void
) {
  return (columnId: string) => {
    const tempFilter = tempNumericFilters[columnId];
    setColumnFilters({
      ...columnFilters,
      [columnId]: {
        ...columnFilters[columnId],
        min: tempFilter?.min !== undefined ? tempFilter.min : null,
        max: tempFilter?.max !== undefined ? tempFilter.max : null,
      },
    });
    closeFilter(columnId);
  };
}

// ----------------------------------------------------------------------------
// Clear filter / clear all (used by DRIInsights Individual Details)
// ----------------------------------------------------------------------------

/**
 * Create clear single column filter handler.
 */
export function createClearFilterHandler(
  setColumnFilters: (v: Record<string, ColumnFilterState>) => void,
  columnFilters: Record<string, ColumnFilterState>,
  closeFilter: (columnId: string) => void
) {
  return (columnId: string) => {
    const newFilters = { ...columnFilters };
    delete newFilters[columnId];
    setColumnFilters(newFilters);
    closeFilter(columnId);
  };
}

/**
 * Create clear all filters handler (resets filters and all temp state).
 */
export function createClearAllFiltersHandler(
  setColumnFilters: (v: Record<string, ColumnFilterState>) => void,
  setFilterAnchorEl: (v: Record<string, HTMLElement | null>) => void,
  setTempSelectedValues: (v: Record<string, string[]>) => void,
  setTempNumericFilters: (v: Record<string, { min?: number | null; max?: number | null }>) => void,
  setFilterSearchText: (v: Record<string, string>) => void
) {
  return () => {
    setColumnFilters({});
    setFilterAnchorEl({});
    setTempSelectedValues({});
    setTempNumericFilters({});
    setFilterSearchText({});
  };
}

// ----------------------------------------------------------------------------
// Checkbox filter handlers (used by DRIInsights Individual Details)
// ----------------------------------------------------------------------------

/**
 * Create checkbox filter toggle handler (temp selected values).
 */
export function createCheckboxFilterToggleHandler(
  setTempSelectedValues: (v: Record<string, string[]>) => void,
  tempSelectedValues: Record<string, string[]>,
  columnFilters: Record<string, ColumnFilterState>,
  columnId: string
) {
  return (value: string) => {
    const currentTemp = tempSelectedValues[columnId] || [];
    const currentApplied = columnFilters[columnId]?.selectedValues || [];
    const base = currentTemp.length > 0 ? currentTemp : currentApplied;
    const next = base.includes(value) ? base.filter((v) => v !== value) : [...base, value];
    setTempSelectedValues({ ...tempSelectedValues, [columnId]: next });
  };
}

/**
 * Create apply checkbox filter handler (commit temp to filters, clear temp, close).
 */
export function createApplyCheckboxFilterHandler(
  setColumnFilters: (v: Record<string, ColumnFilterState>) => void,
  columnFilters: Record<string, ColumnFilterState>,
  setTempSelectedValues: (v: Record<string, string[]>) => void,
  tempSelectedValues: Record<string, string[]>,
  setFilterSearchText: (v: Record<string, string>) => void,
  filterSearchText: Record<string, string>,
  closeFilter: (columnId: string) => void
) {
  return (columnId: string) => {
    const selectedValues = tempSelectedValues[columnId] || [];
    setColumnFilters({
      ...columnFilters,
      [columnId]: {
        ...columnFilters[columnId],
        selectedValues: selectedValues.length > 0 ? selectedValues : undefined,
        text: undefined,
      },
    });
    const newTemp = { ...tempSelectedValues };
    delete newTemp[columnId];
    setTempSelectedValues(newTemp);
    const newSearch = { ...filterSearchText };
    delete newSearch[columnId];
    setFilterSearchText(newSearch);
    closeFilter(columnId);
  };
}

/**
 * Create select all checkbox filter handler.
 */
export function createSelectAllCheckboxFilterHandler(
  setTempSelectedValues: (v: Record<string, string[]>) => void,
  tempSelectedValues: Record<string, string[]>,
  columnId: string
) {
  return (filteredValues: string[]) => {
    setTempSelectedValues({ ...tempSelectedValues, [columnId]: filteredValues });
  };
}

/**
 * Create clear all checkbox filter handler (clear temp for column).
 */
export function createClearAllCheckboxFilterHandler(
  setTempSelectedValues: (v: Record<string, string[]>) => void,
  tempSelectedValues: Record<string, string[]>,
  columnId: string
) {
  return () => {
    setTempSelectedValues({ ...tempSelectedValues, [columnId]: [] });
  };
}

// ============================================================================
// End of FilterUtils – used by src/components/dri-insights/DRIInsights.tsx
// ============================================================================
